import React from "react";
import { DEFECT_IMAGE_SKILL_FOLDER_URL } from "constants/pipelines";
import type { LaunchableContentTabProps } from "components/Launchables/DeployRedesign/shared/content/tabs/LaunchableContentTab";
import {
  InlineLink,
  Paragraph,
  PromptExample,
  SectionHeading,
} from "../shared";
import { PhysicalAISkin } from "../PhysicalAISkin";
const DEFECT_IMAGE_HEADER = {
  title: "Defect Image Generation",
  summary:
    "Scalable synthetic defect generation for high-accuracy Automated Optical Inspection (AOI) — agent and skills driven from a prompt.",
  badges: [
    "NVIDIA Cosmos",
    "NVIDIA Isaac Sim",
    "Physical AI skills for Synthetic Data Generation",
  ],
} as const;

const DEFECT_IMAGE_PIPELINE_FEATURES = [
  [
    "Defect image Generation Skill",
    "Coordinates flow selection, preflight checks, setup, workflow submission, monitoring, and output retrieval.",
  ],
  [
    "Simulation / USD-to-ROI",
    "Uses Isaac Sim and USD assets to render clean PCBA regions, scan-grid crops, and structural pose defects such as shift, tombstone, and sideflip.",
  ],
  [
    "Qwen Image Edit Augmentation",
    "Transfers Omniverse-rendered crops into more realistic inspection imagery, including solder-lighting appearance transfer for PCBA workflows.",
  ],
  [
    "Cosmos AnomalyGen",
    "Generates realistic texture defects such as solder bridges, scratches, discoloration, missing components, metal cracks, pits, chips, contamination, and glass surface defects. It can use shipped checkpoints or fine-tune on a labeled defect dataset.",
  ],
  [
    "OSMO Infrastructure",
    "Runs setup, training, inference, monitoring, and download workflows on scalable infrastructure using the configured pool, storage profile, and GPU resources.",
  ],
] as const;

export const DefectImageHeader: React.FC = () => (
  <div className="flex flex-col">
    <h1 className="text-white text-3xl font-semibold">
      {DEFECT_IMAGE_HEADER.title}
    </h1>
    <p className="text-secondary text-sm mt-2">{DEFECT_IMAGE_HEADER.summary}</p>
    <div className="flex items-center gap-2 flex-wrap mt-4">
      {DEFECT_IMAGE_HEADER.badges.map((badge) => (
        <span
          key={badge}
          className="text-xs text-white/70 border border-zinc-700 px-2.5 py-1 rounded-full"
        >
          {badge}
        </span>
      ))}
    </div>
  </div>
);

const DefectImageOverview: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        Defect Image Generation enables at-scale synthetic defect data
        generation for high-accuracy Automated Optical Inspection (AOI). Powered
        by NVIDIA Cosmos, NVIDIA Isaac Sim, and NVIDIA OSMO, the launchable
        generates realistic defect examples for PCBA, metal, and glass
        inspection workflows so teams can post-train visual inspection models
        without waiting months for rare production defects.
      </Paragraph>
    </div>

    <div className="aspect-video overflow-hidden rounded-md border border-zinc-800 bg-black">
      <iframe
        className="h-full w-full"
        src="https://www.youtube.com/embed/hREG6r-fogo"
        title="Generating Synthetic Defect Images for Visual Inspection With Open Source Physical AI Agent Skills"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>

    <div>
      <SectionHeading>Agent Experience</SectionHeading>
      <Paragraph>
        Open the launchable on Brev, click the Claw link from the instance
        access page, and type a natural-language goal such as:
      </Paragraph>
      <div className="flex flex-col gap-3 mt-3">
        <PromptExample>
          “Generature textural defects for component _115-2819-000 with full
          board coverage to generate bridge defects.”
        </PromptExample>
        <PromptExample>
          “Generate shift structural defects for 115_2819_000, with full board
          coverage.”
        </PromptExample>
        <PromptExample>
          “Run the day 1 defect image generation workflow to generate images of
          metal surface defects using the shipped metal checkpoint and generate
          all the defect types.”
        </PromptExample>
        <PromptExample>
          “Run day 1 defect image generation workflow for glass using the
          packaged sample assets. Generate oil, scratch, and stain defects with
          the shipped glass checkpoint.”
        </PromptExample>
      </div>
      <Paragraph>
        Before the first run, have the required credentials ready: an NVIDIA
        Build API key for Claw, a Hugging Face read token with access to the
        required gated models and datasets. For the glass workflow, also prepare
        the required glass source zip before setup. The claw tool calls view can
        be toggled on / off from the claw UI per user preference.
      </Paragraph>
      <p className="text-sm text-white/70 leading-relaxed mt-3">
        <span className="font-semibold text-white/90">
          Time to first output:
        </span>{" "}
        about 30 minutes to an hour depending on the setup and selected
        workflow.
      </p>
      <p className="text-sm font-semibold text-white/90 mt-3">The Agent:</p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-3">
        <li>
          Asks clarifying questions about the surface, flow, available
          components, defect type, image count, checkpoint, and endpoint only
          when those choices change execution.
        </li>
        <li>
          Validates OSMO login, storage, credentials, pod template, shared
          memory, NVOptiX, and required dataset/model artifacts.
        </li>
        <li>
          Runs setup workflows to prepare PCBA, metal, and glass sample assets,
          pretrained checkpoints, USD assets, and optionally a local Qwen Image
          Edit endpoint.
        </li>
        <li>
          Submits and monitors OSMO workflows for USD rendering, image-edit
          augmentation, Cosmos AnomalyGen training or inference, labeling, and
          output retrieval.
        </li>
        <li>
          Returns workflow IDs, output paths, logs, and preview-ready artifacts
          for review or downstream model training.
        </li>
      </ul>
    </div>

    <div>
      <SectionHeading>Pipeline Features</SectionHeading>
      <div className="grid grid-cols-1 gap-2">
        {DEFECT_IMAGE_PIPELINE_FEATURES.map(([feature, detail], i) => (
          <div
            key={feature}
            className="flex gap-3 items-start rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-3 transition-colors"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[10px] font-bold text-white/60">
              {i + 1}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-white/90">
                {feature}
              </span>
              <span className="text-xs text-white/55 leading-relaxed">
                {detail}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const DefectImageDetails: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        Modern Automated Optical Inspection (AOI) systems rely on AI models
        trained to detect rare, high-impact defects. However, real defect data
        is scarce, expensive, and slow to collect. Defect Image Generation helps
        close that gap by combining NVIDIA Isaac Sim, NVIDIA Cosmos, and OSMO
        workflows to produce realistic synthetic defect images, masks, crops,
        labels, and run metadata for visual inspection model development.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>What it does</SectionHeading>
      <Paragraph>
        The workflow starts from clean inspection images, USD scenes, ROI masks,
        or prepared AOI datasets depending on the selected flow. It can render
        clean PCBA examples from a digital twin, synthesize structural pose
        defects, apply image-edit based Sim2Real lighting transfer, generate
        texture anomalies with Cosmos AnomalyGen, and emit labeled outputs for
        review, download, or downstream model training. Generated outputs can
        include reconstructed images, annotated images, cropped images, masks,
        original images, USD-derived ROI artifacts, and SDG_result.csv metadata.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>Getting Started</SectionHeading>
      <p className="text-sm font-semibold text-white/90 mb-2">
        1. Prerequisites
      </p>
      <Paragraph>
        Before running any workflows, open Claw or your preferred coding agent
        in the deployed environment and gather the following:
      </Paragraph>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>
          <span className="font-semibold text-white/90">
            NVIDIA Build API Key (or Agent API Key):
          </span>{" "}
          To power endpoint-backed model calls.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Hugging Face Token:
          </span>{" "}
          For model and dataset downloads (
          <InlineLink href="https://github.com/NVIDIA/physical-ai-data-factory/blob/main/docs/workflows/physical-ai-defect-image-generation/launchable.md#before-you-run">
            you must accept the licenses on the individual repos first
          </InlineLink>
          ).
        </li>
        <li>
          <span className="font-semibold text-white/90">
            (Optional) Glass defect data:
          </span>{" "}
          Additional data needed for glass usecase.{" "}
          <InlineLink href="https://github.com/NVIDIA/physical-ai-data-factory/blob/main/docs/workflows/physical-ai-defect-image-generation/media/glass_dataset_download_instructions.md">
            See instructions
          </InlineLink>
          .
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4 mb-2">
        2. Setup Agent Workflow
      </p>
      <Paragraph>
        Once your keys are ready, paste the following prompt into OpenClaw or
        your coding agent&apos;s chat:
      </Paragraph>
      <div className="mt-3">
        <PromptExample>
          “Setup the defect image generation workflow pre-requisites for all use
          cases including a local qwen image edit endpoint. My glass zip is
          located at{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            /home/ubuntu/mobile_screen.zip
          </code>
          . Ensure nvoptix and shm requirements are sufficient for running the
          workflows. Here is my huggingface token:{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;your-hf-token&gt;
          </code>
          .”
        </PromptExample>
      </div>
      <p className="text-sm italic text-white/70 leading-relaxed mt-3">
        Setup takes about 12 minutes on the default configured environment. When
        finished, you can prompt the agent with{" "}
        <code className="text-white/85 bg-zinc-800 px-1 rounded">
          /bootstrap
        </code>{" "}
        to see supported flows.
      </p>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Running the Workflows</SectionHeading>
      <p className="text-sm font-semibold text-white/90 mb-2">
        Day 0: Generate defect images from simulation
      </p>
      <Paragraph>
        Targets the earliest stage of a PCBA project when the board exists in
        USD format, but you might not yet have real inspection images. It uses
        Isaac Sim to render images of components and Qwen-Image-Edit to improve
        photorealism.
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Example Prompts:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="italic">Good Images:</span> “Generate good images of{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            _0603-H100
          </code>{" "}
          asset, set max image emit count to 3.”
        </li>
        <li>
          <span className="italic">Structural Defects:</span> “Generate shift
          structural defects for{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            _115_2819_000
          </code>
          , with full board coverage.”
        </li>
        <li>
          <span className="italic">Textural Defects:</span> “Run Day 0 for
          component{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            _0603_H100
          </code>{" "}
          to generate excess solder and missing defects.”
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4 mb-2">
        Day 1: Generate defect images from real data
      </p>
      <Paragraph>
        Applies when you already have clean images, ROI masks, or a real AOI
        screenshots.
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Example Prompts:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="italic">Metal:</span> “Run the day 1 defect image
          generation workflow to generate images of metal surface defects using
          the shipped metal checkpoint and generate all the defect types it
          ships with.”
        </li>
        <li>
          <span className="italic">Glass:</span> “Run day 1 defect image
          generation workflow for glass using the packaged sample assets.
          Generate oil, scratch, and stain defects with the shipped glass
          checkpoint.”
        </li>
        <li>
          <span className="italic">PCBA:</span> “Run Day 1 for component{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            _115-2819-000
          </code>{" "}
          with its real image to generate bridge defects.”
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4 mb-2">
        Finetune Anomalygen
      </p>
      <Paragraph>
        When you want an AnomalyGen adapted to your own labeled dataset before
        generation, ask the agent to run Day 0 or Day 1 workflows with
        finetuning enabled. This pipeline first trains a fresh AnomalyGen
        checkpoint against the prepared dataset, then immediately runs
        generation with the result
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3 mb-2">
        Example Prompt:
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          “Run the day 1 workflow to generate images of metal surface defects.
          Finetune with the default metal dataset to 10000 steps and use
          10000-step checkpoint to generate at most all the defect types.”
        </li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Useful Prompts:</SectionHeading>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          “What&apos;s the status of my last Defect Image Generation workflow?”
        </li>
        <li>
          “Show me the logs for the anomaly-infer task in workflow{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;workflow-id&gt;
          </code>
          .”
        </li>
        <li>
          “Download the outputs of all groups of workflow{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;workflow-id&gt;
          </code>{" "}
          to{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            /home/ubuntu/dig-results
          </code>
          .”
        </li>
        <li>
          “Monitor workflow{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;workflow-id&gt;
          </code>{" "}
          every 2 minutes and report back once it is done with a quick preview
          grid image of the original image, usd roi generated cad masks, AMP
          placed masks along with the reconstructed anomalygen result.”
        </li>
      </ul>

      <p className="text-sm font-semibold text-white/90 mt-4">
        Typical Outputs Include:
      </p>
      <Paragraph>
        Reconstructed and annotated images, cropped images/masks, original
        images/masks, USD-to-ROI outputs, and an{" "}
        <code className="text-white/85 bg-zinc-800 px-1 rounded">
          SDG_result.csv
        </code>{" "}
        containing labels and run metadata.
      </Paragraph>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>
        Best Practices: Getting the Most Out of the Agent
      </SectionHeading>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="font-semibold text-white/90">
            Be explicit about the use case:
          </span>{" "}
          Name the surface (PCBA / metal / glass) and the flow (Day 0 / Day 1 /
          Finetune) up front.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            State your bounds:
          </span>{" "}
          Mention the component, defect types, image counts, and finetune step
          caps in the same prompt to avoid the agent picking unwanted defaults.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Hand it credentials inline:
          </span>{" "}
          Paste your HF token, zip paths, or endpoint URLs directly into the
          prompt to skip back-and-forth chatter.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Let long-running jobs run as subagents:
          </span>{" "}
          Submission and download tasks default to subagents. Ask things like{" "}
          <span className="italic">
            “Monitor workflow{" "}
            <code className="text-white/85 bg-zinc-800 px-1 rounded">
              &lt;id&gt;
            </code>{" "}
            and report back when it&apos;s done”
          </span>{" "}
          so your main chat stays responsive.
        </li>
        <li>
          <span className="font-semibold text-white/90">
            When in doubt, ask the agent:
          </span>{" "}
          The agent has all the skills loaded, so the fastest way to answer your
          questions is usually just by asking.
        </li>
      </ul>
    </div>

    <hr className="border-zinc-800" />

    <div>
      <SectionHeading>Frequently Asked Questions</SectionHeading>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Why is synthetic defect data needed?
          </p>
          <Paragraph>
            A: Real-world defects are rare, expensive, and slow to collect. Many
            manufacturing teams do not have enough labeled samples for every
            defect type they need to detect. Synthetic defect generation fills
            that gap by producing controllable, reviewable examples without
            waiting for production-line defects to appear.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: What is the Defect Image Generation launchable?
          </p>
          <Paragraph>
            A: It is a ready-to-use Brev environment for running agent-guided
            OSMO workflows that generate synthetic defect data for AOI. It
            combines workflow skills for Isaac Sim rendering, Qwen image-edit
            augmentation, Cosmos AnomalyGen training or inference,
            infrastructure setup, monitoring, and output retrieval.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: What materials and defects does it support?
          </p>
          <Paragraph>
            A: The launchable includes practical workflows for PCBA, metal, and
            glass inspection. PCBA workflows support clean images, structural
            pose defects, texture defects, and missing components. Metal and
            glass workflows support surface anomalies such as scratches, cracks,
            pits, chips, oil, stain, and contamination depending on the selected
            shipped assets and checkpoint.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Do I need a large database of real defects?
          </p>
          <Paragraph>
            A: No. You can start with shipped sample assets and pretrained
            checkpoints for PCBA, metal, and glass. If you have a niche defect
            family, you can fine-tune AnomalyGen with a labeled dataset and
            reuse the resulting checkpoint for generation.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: What is solder RGB lighting and why does it matter?
          </p>
          <Paragraph>
            A: Many AOI systems use specialized RGB lighting to expose solder
            geometry and surface conditions. The PCBA Day 0 workflow uses
            simulation and image-edit augmentation to better match the lighting
            and appearance of real inspection imagery.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: Can I use my own coding agent?
          </p>
          <Paragraph>
            A: Yes. You can use the Brev launchable with Claw/OpenClaw or{" "}
            <InlineLink href="https://github.com/NVIDIA/skills/tree/main/skills/physical-ai-defect-image-generation">
              download the skills from GitHub
            </InlineLink>{" "}
            and run them with another supported coding agent.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: How does this differ from traditional synthetic data generation?
          </p>
          <Paragraph>
            A: Traditional synthetic data workflows often struggle with realism,
            spatial accuracy, and inspection-lighting consistency. This
            launchable combines USD-based rendering, image-edit based Sim2Real
            transfer, and Cosmos AnomalyGen to produce controllable outputs
            grounded in real images, masks, and anomaly semantics.
          </Paragraph>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-white/90">
            Q: How do I get started?
          </p>
          <Paragraph>
            A: Deploy the Brev launchable, open Claw from Secure Links, complete
            setup with your NVIDIA Build API Key and Hugging Face credentials,
            then start from one of the example prompts.
          </Paragraph>
        </div>
      </div>
    </div>
  </div>
);

export const DefectImageSkin: React.FC<LaunchableContentTabProps> = (props) => (
  <PhysicalAISkin
    {...props}
    skillFolderUrl={DEFECT_IMAGE_SKILL_FOLDER_URL}
    overview={<DefectImageOverview />}
    details={<DefectImageDetails />}
  />
);
